export const capturePhoto = async (): Promise<string | null> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });

    const video = document.createElement('video');
    video.srcObject = stream;
    video.autoplay = true;
    video.playsInline = true;

    await new Promise((resolve) => {
      video.onloadedmetadata = () => {
        video.play();
        resolve(null);
      };
    });

    await new Promise((resolve) => setTimeout(resolve, 500));

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Process image: crop to square (1:1) and resize to 1024×1024
    const sourceWidth = canvas.width;
    const sourceHeight = canvas.height;
    const sourceSize = Math.min(sourceWidth, sourceHeight); // Use smaller dimension for square crop

    // Calculate crop position (center the crop)
    const sourceX = (sourceWidth - sourceSize) / 2;
    const sourceY = (sourceHeight - sourceSize) / 2;

    // Create new canvas for processed image (1024×1024 square)
    const processedCanvas = document.createElement('canvas');
    processedCanvas.width = 1024;
    processedCanvas.height = 1024;

    const processedCtx = processedCanvas.getContext('2d');
    if (!processedCtx) {
      throw new Error('Failed to get processed canvas context');
    }

    // Draw cropped and resized image
    processedCtx.drawImage(
      canvas,
      sourceX, // Source x (crop start)
      sourceY, // Source y (crop start)
      sourceSize, // Source width (crop size)
      sourceSize, // Source height (crop size)
      0, // Destination x
      0, // Destination y
      1024, // Destination width
      1024, // Destination height
    );

    const blob = await new Promise<Blob>((resolve, reject) => {
      processedCanvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert processed canvas to blob'));
          }
        },
        'image/jpeg',
        0.9, // High quality JPEG
      );
    });

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read blob as data URL'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    stream.getTracks().forEach((track) => track.stop());
    video.srcObject = null;

    return dataUrl;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error('[Photo Capture] Error:', err);
    }
    return null;
  }
};
